import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, Plus, Printer, Target } from 'lucide-react';
import { toast } from 'sonner';

export default function IMadeItMean() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem('iMadeItMeanWorksheet');
    return saved ? JSON.parse(saved) : [{ event: '', meaning: '' }, { event: '', meaning: '' }, { event: '', meaning: '' }];
  });

  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  const saveProgress = () => { localStorage.setItem('iMadeItMeanWorksheet', JSON.stringify(entries)); toast.success('Saved!'); };
  const addEntry = () => setEntries([...entries, { event: '', meaning: '' }]);
  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx][field] = value;
    setEntries(updated);
  };

  useEffect(() => {
    const interval = setInterval(() => localStorage.setItem('iMadeItMeanWorksheet', JSON.stringify(entries)), 30000);
    return () => clearInterval(interval);
  }, [entries]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">I Made It Mean... Worksheet</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <Button onClick={addEntry} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white"><Plus className="h-4 w-4 mr-2" />Add</Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white"><Save className="h-4 w-4 mr-2" />Save</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 52</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8"><Target className="h-16 w-16 text-amber-400 mx-auto mb-4" /><h2 className="text-3xl font-bold text-white mb-4">I Made It Mean... Worksheet</h2></div>
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6 mb-8">
            <p className="text-amber-100 leading-relaxed mb-3">
              Since all the meaning we make of things is totally subjective, it is important to be able to notice what we are making things mean so we can be more at choice about how we would like to respond.
            </p>
            <p className="text-amber-200 font-semibold">The more you practice this awareness, the more you will be a powerful creator of your life.</p>
          </div>
          <div className="space-y-6">
            {entries.map((entry, idx) => (
              <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4">Entry #{idx + 1}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-blue-300 text-sm mb-2 block">Somebody said to me / I saw someone do:</label>
                    <Textarea value={entry.event} onChange={(e) => updateEntry(idx, 'event', e.target.value)} placeholder='e.g., "You never help around the house"' className="bg-slate-900/40 border-slate-700 text-white min-h-[60px]" />
                  </div>
                  <div>
                    <label className="text-purple-300 text-sm mb-2 block">And I made it mean:</label>
                    <Textarea value={entry.meaning} onChange={(e) => updateEntry(idx, 'meaning', e.target.value)} placeholder="The story I told myself about what it meant..." className="bg-purple-900/20 border-purple-700/30 text-white min-h-[80px]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-400 text-sm mt-6">💾 Auto-saves every 30 seconds</p>
          <p className="text-center text-slate-500 text-xs mt-4">Steve Torma • www.theREALcenter.org • 828-254-5613</p>
        </div>
      </div>
    </div>
  );
}
